import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAdminAuth } from "../AdminAuthContext";
import {
  PageHeader, Panel, Badge, Table, StatCard,
} from "../components/UIKit";
import LiveMonitor from "../components/LiveMonitor";
import {
  ArrowLeft, User as UserIcon, Mic, Ruler, Weight, Heart, BookOpen, Speaker,
} from "lucide-react";

const MIC_LABELS = {
  granted: { label: "Concesso", color: "teal" },
  denied: { label: "Negato", color: "rose" },
  skipped: { label: "Saltato", color: "slate" },
  prompt: { label: "Da chiedere", color: "amber" },
  unknown: { label: "Sconosciuto", color: "slate" },
};

function formatDateIt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatDate(date) {
  if (!date) return "—";
  try {
    const d = new Date(date);
    return d.toLocaleDateString("it-IT", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch {
    return date;
  }
}

function calcAge(birthDate) {
  if (!birthDate) return null;
  try {
    const bd = new Date(birthDate);
    const diff = Date.now() - bd.getTime();
    return Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
  } catch {
    return null;
  }
}

function ProfileRow({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-center gap-3 py-2 border-b border-white/5 last:border-b-0">
      {Icon ? (
        <span className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center text-white/60">
          <Icon size={14} />
        </span>
      ) : null}
      <div className="text-white/55 text-[11px] uppercase tracking-wider w-32">
        {label}
      </div>
      <div className={`text-white text-[13px] ${mono ? "font-mono" : ""}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}

export default function AdminUserDetail() {
  const { id } = useParams();
  const { api } = useAdminAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api(`/users/${id}`)
      .then(setData)
      .catch((e) => setErr(e?.message || "Errore caricamento utente"));
  }, [id, api]);

  if (err) return <div className="p-8 text-rose-300 text-sm">{err}</div>;
  if (!data) return <div className="p-8 text-white/60">Caricamento…</div>;

  const u = data.user || {};
  const prefs = data.preferences || {};
  const consents = data.consents || {};
  const stats = data.stats || {};
  const sessions = data.sessions || [];
  const journal = data.journal_entries || [];
  const clips = data.audio_clips || [];
  const devices = data.devices || [];

  const micBadge = MIC_LABELS[prefs.mic_permission] || MIC_LABELS.unknown;
  const age = calcAge(u.birth_date);

  const avgScore =
    sessions.length > 0
      ? Math.round(
          sessions.reduce((s, r) => s + (r.score_integrated || r.score_mattress || 0), 0) /
            sessions.length
        )
      : 0;

  return (
    <div className="p-8">
      <button
        onClick={() => nav(-1)}
        className="mb-3 inline-flex items-center gap-2 text-white/70 hover:text-white text-[12px]"
      >
        <ArrowLeft size={14} /> Torna agli utenti
      </button>

      <PageHeader
        title={`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email}
        subtitle={u.email}
        actions={
          <div className="flex gap-2">
            <Badge color={u.source === "real" ? "teal" : "slate"}>
              {u.source === "real" ? "Utente reale" : "Demo"}
            </Badge>
            <Badge color={u.status === "online" ? "teal" : "slate"}>
              {u.status || "active"}
            </Badge>
          </div>
        }
      />

      {/* Stats overview */}
      <div className="grid grid-cols-4 gap-3" data-testid="admin-user-stats">
        <StatCard label="Sessioni totali" value={stats.sessions_total ?? 0} />
        <StatCard
          label="Score medio"
          value={avgScore || "—"}
          sub="ultime 60 sessioni"
          accent="sky"
        />
        <StatCard
          label="Voci diario"
          value={stats.journal_entries_total ?? 0}
          accent="teal"
        />
        <StatCard
          label="Clip audio"
          value={stats.audio_clips_total ?? 0}
          sub={`${stats.devices_active ?? 0} device attivi`}
          accent="amber"
        />
      </div>

      {/* Profilo + Preferenze */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Profilo">
          <div className="p-4">
            <ProfileRow icon={UserIcon} label="Nome" value={u.first_name} />
            <ProfileRow label="Cognome" value={u.last_name} />
            <ProfileRow label="Email" value={u.email} mono />
            <ProfileRow label="ID" value={u.id} mono />
            <ProfileRow label="Sesso" value={u.gender} />
            <ProfileRow
              label="Data nascita"
              value={
                u.birth_date
                  ? `${formatDate(u.birth_date)}${age != null ? ` (${age} anni)` : ""}`
                  : null
              }
            />
            <ProfileRow label="Registrato il" value={formatDateIt(u.created_at)} />
            <ProfileRow label="Ultimo aggiornamento" value={formatDateIt(u.updated_at)} />
          </div>
        </Panel>

        <Panel title="Preferenze">
          <div className="p-4">
            <div className="flex items-center gap-3 py-2 border-b border-white/5">
              <span className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center text-white/60">
                <Mic size={14} />
              </span>
              <div className="text-white/55 text-[11px] uppercase tracking-wider w-32">
                Permesso microfono
              </div>
              <Badge color={micBadge.color}>{micBadge.label}</Badge>
              {prefs.mic_permission_set_at && (
                <span className="text-white/40 text-[11px] ml-2">
                  da {formatDateIt(prefs.mic_permission_set_at)}
                </span>
              )}
            </div>
            <ProfileRow
              icon={Speaker}
              label="Consenso clip audio"
              value={
                prefs.audio_clips_consent === true
                  ? "Sì (clip salvabili)"
                  : prefs.audio_clips_consent === false
                  ? "No"
                  : "Non specificato"
              }
            />
            <ProfileRow
              icon={Ruler}
              label="Altezza"
              value={prefs.height_cm ? `${prefs.height_cm} cm` : null}
            />
            <ProfileRow
              icon={Weight}
              label="Peso"
              value={prefs.weight_kg ? `${prefs.weight_kg} kg` : null}
            />
          </div>
        </Panel>
      </div>

      {/* Consensi GDPR + Reset password (solo per utenti reali) */}
      {u.source === "real" && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Panel title="Consensi GDPR" right={<Heart size={14} className="text-white/45" />}>
            <div className="p-4 space-y-3">
              <ConsentRow
                label="Termini di Servizio & Privacy"
                accepted={consents.terms}
                at={consents.terms_at}
                required
              />
              <ConsentRow
                label="Marketing"
                accepted={consents.marketing}
                at={consents.marketing_at}
              />
              <ConsentRow
                label="Analisi dati per ricerca"
                accepted={consents.analytics}
                at={consents.analytics_at}
              />
            </div>
          </Panel>
          <Panel title="Reset password" right={<UserIcon size={14} className="text-white/45" />}>
            <AdminPasswordReset userId={u.id} api={api} />
          </Panel>
          <div className="hidden lg:block" />
        </div>
      )}

      {/* Live monitor */}
      <div className="mt-6">
        <LiveMonitor userId={id} />
      </div>

      {/* Diario */}
      <div className="mt-6">
        <Panel
          title={`Diario (${journal.length}${stats.journal_entries_total > journal.length ? ` su ${stats.journal_entries_total}` : ""})`}
          right={<BookOpen size={14} className="text-white/45" />}
        >
          {journal.length === 0 ? (
            <div className="p-6 text-white/50 text-sm text-center">
              L'utente non ha ancora compilato note nel diario.
            </div>
          ) : (
            <Table
              columns={[
                {
                  key: "date",
                  label: "Data",
                  render: (r) => formatDate(r.date),
                },
                {
                  key: "mood",
                  label: "Umore",
                  render: (r) =>
                    r.mood != null ? (
                      <span className="font-mono">{r.mood}/10</span>
                    ) : "—",
                },
                {
                  key: "energy",
                  label: "Energia",
                  render: (r) =>
                    r.energy != null ? (
                      <span className="font-mono">{r.energy}/10</span>
                    ) : "—",
                },
                {
                  key: "stress",
                  label: "Stress",
                  render: (r) =>
                    r.stress != null ? (
                      <span className="font-mono">{r.stress}/10</span>
                    ) : "—",
                },
                {
                  key: "sport",
                  label: "Sport",
                  render: (r) => r.sport || "—",
                },
                {
                  key: "habits",
                  label: "Abitudini",
                  render: (r) => (
                    <div className="flex gap-1.5 flex-wrap">
                      {r.caffeine ? <Badge color="amber">Caffeina</Badge> : null}
                      {r.alcohol ? <Badge color="rose">Alcol</Badge> : null}
                      {r.screen_time ? <Badge color="slate">{r.screen_time}</Badge> : null}
                    </div>
                  ),
                },
                {
                  key: "notes",
                  label: "Note",
                  render: (r) => (
                    <span className="text-white/75 text-[12px] line-clamp-2 max-w-[280px] block">
                      {r.notes || "—"}
                    </span>
                  ),
                },
              ]}
              rows={journal}
            />
          )}
        </Panel>
      </div>

      {/* Audio clips */}
      <div className="mt-6">
        <Panel
          title={`Clip audio registrate (${clips.length}${stats.audio_clips_total > clips.length ? ` su ${stats.audio_clips_total}` : ""})`}
          right={<Heart size={14} className="text-white/45" />}
        >
          {clips.length === 0 ? (
            <div className="p-6 text-white/50 text-sm text-center">
              Nessuna clip audio registrata.
              {prefs.mic_permission !== "granted"
                ? " (Microfono non autorizzato)"
                : prefs.audio_clips_consent !== true
                ? " (Consenso clip non dato)"
                : ""}
            </div>
          ) : (
            <Table
              columns={[
                {
                  key: "ts_ms",
                  label: "Timestamp",
                  render: (r) =>
                    r.ts_ms
                      ? new Date(r.ts_ms).toLocaleString("it-IT")
                      : "—",
                },
                { key: "label", label: "Etichetta" },
                {
                  key: "duration_ms",
                  label: "Durata",
                  render: (r) =>
                    r.duration_ms
                      ? `${(r.duration_ms / 1000).toFixed(1)}s`
                      : "—",
                },
                { key: "session_id", label: "Sessione", render: (r) => <span className="font-mono text-[11px]">{r.session_id?.slice(0, 12) || "—"}</span> },
              ]}
              rows={clips}
            />
          )}
        </Panel>
      </div>

      {/* Devices */}
      <div className="mt-6">
        <Panel title="Dispositivi associati">
          {devices.length === 0 ? (
            <div className="p-6 text-white/50 text-sm text-center">
              Nessun dispositivo SmartDream associato.
            </div>
          ) : (
            <Table
              columns={[
                { key: "mac_address", label: "MAC", render: (r) => <span className="font-mono">{r.mac_address}</span> },
                { key: "mattress_name", label: "Nome materasso" },
                {
                  key: "paired_at",
                  label: "Accoppiato il",
                  render: (r) => formatDateIt(r.paired_at),
                },
                {
                  key: "active",
                  label: "Stato",
                  render: (r) => (
                    <Badge color={r.active ? "teal" : "slate"}>
                      {r.active ? "attivo" : "disattivo"}
                    </Badge>
                  ),
                },
              ]}
              rows={devices}
            />
          )}
        </Panel>
      </div>

      {/* Storico sessioni */}
      <div className="mt-6">
        <Panel title={`Storico sessioni (${sessions.length})`}>
          {sessions.length === 0 ? (
            <div className="p-6 text-white/50 text-sm text-center">
              L'utente non ha ancora completato sessioni di sonno.
            </div>
          ) : (
            <Table
              columns={[
                {
                  key: "started_at",
                  label: "Data",
                  render: (r) =>
                    r.started_at ? formatDateIt(r.started_at) : "—",
                },
                {
                  key: "duration_min",
                  label: "Durata",
                  render: (r) => {
                    const m = r.duration_min || 0;
                    return `${Math.floor(m / 60)}h ${m % 60}m`;
                  },
                },
                {
                  key: "sample_count",
                  label: "Campioni",
                  render: (r) => (
                    <span className="font-mono">{r.sample_count || 0}</span>
                  ),
                },
                {
                  key: "status",
                  label: "Stato",
                  render: (r) => (
                    <Badge color={r.status === "completed" ? "teal" : "amber"}>
                      {r.status || "—"}
                    </Badge>
                  ),
                },
              ]}
              rows={sessions}
            />
          )}
        </Panel>
      </div>
    </div>
  );
}

function ConsentRow({ label, accepted, at, required }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-white/5 last:border-0">
      <div className="flex-1">
        <div className="text-white/90 text-[13px] font-medium flex items-center gap-1.5">
          {label}
          {required && (
            <span className="text-[10px] uppercase tracking-wider text-amber-300/80">
              obblig.
            </span>
          )}
        </div>
        <div className="text-white/45 text-[11px] mt-0.5">
          {at ? formatDateIt(at) : "—"}
        </div>
      </div>
      <Badge color={accepted ? "teal" : "slate"}>{accepted ? "Sì" : "No"}</Badge>
    </div>
  );
}

function AdminPasswordReset({ userId, api }) {
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (pwd.length < 4) {
      setMsg({ kind: "err", text: "Password minimo 4 caratteri." });
      return;
    }
    if (pwd !== pwd2) {
      setMsg({ kind: "err", text: "Le due password non coincidono." });
      return;
    }
    if (!window.confirm("Confermi il cambio password per questo utente?")) return;
    setLoading(true);
    try {
      await api(`/users/${userId}/password`, {
        method: "PATCH",
        data: { password: pwd },
      });
      setMsg({ kind: "ok", text: "Password aggiornata correttamente." });
      setPwd("");
      setPwd2("");
    } catch (err) {
      setMsg({
        kind: "err",
        text:
          err?.response?.data?.detail ||
          "Errore durante il cambio password.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="p-4 space-y-3" data-testid="admin-pwd-reset-form">
      <div className="text-white/55 text-[12px] leading-snug">
        Imposta una nuova password per l'utente. L'utente potrà accedere
        immediatamente con la nuova password.
      </div>
      <input
        type="password"
        value={pwd}
        onChange={(e) => setPwd(e.target.value)}
        placeholder="Nuova password"
        data-testid="admin-pwd-new"
        className="w-full h-10 rounded-lg bg-white/5 border border-white/10 px-3 text-white text-sm focus:outline-none focus:border-[#14c2a1]"
      />
      <input
        type="password"
        value={pwd2}
        onChange={(e) => setPwd2(e.target.value)}
        placeholder="Conferma password"
        data-testid="admin-pwd-confirm"
        className="w-full h-10 rounded-lg bg-white/5 border border-white/10 px-3 text-white text-sm focus:outline-none focus:border-[#14c2a1]"
      />
      <button
        type="submit"
        disabled={loading || !pwd}
        data-testid="admin-pwd-submit"
        className="w-full h-10 rounded-lg bg-[#14c2a1] hover:bg-[#0fb094] disabled:opacity-50 text-[#0a3a34] text-sm font-semibold transition-colors"
      >
        {loading ? "Salvataggio…" : "Cambia password"}
      </button>
      {msg && (
        <div
          className={`text-[12px] px-2 py-1.5 rounded ${
            msg.kind === "ok"
              ? "bg-[#14c2a1]/15 text-[#67e8d1] border border-[#14c2a1]/30"
              : "bg-rose-500/15 text-rose-200 border border-rose-400/30"
          }`}
          data-testid="admin-pwd-msg"
        >
          {msg.text}
        </div>
      )}
    </form>
  );
}
